import React, { useState, useEffect, useRef, useCallback } from 'react';
import { APP_VERSION } from './constants';
import { GENRE_LIBRARY } from './data/GenreLibrary';
import { normalizeKey } from './data/theoryConstants'; // Import normalizeKey
import { useMusicEngine } from './hooks/useMusicEngine';
import { useAudioEngine } from './hooks/useAudioEngine';
import { useEnsemble } from './hooks/useEnsemble';
import { exportToMidi } from './utils/MidiExporter';
import ArrangementMatrix from './components/ArrangementMatrix';
import PlaybackVisualizer from './components/PlaybackVisualizer';
import GlobalControlBar from './components/GlobalControlBar';
import DNAModal from './components/DNAModal';
import Knob from './components/Knob';
import StartOverlay from './components/StartOverlay';
import Footer from './components/Footer';
import ContactForm from './components/ContactForm';
import DonateButton from './components/DonateButton';
import PerformanceControlPanel from './components/PerformanceControlPanel';
import MasterEffectsPanel from './components/MasterEffectsPanel'; // NEW

function App() {
  const {
    composition,
    setComposition,
    isGenerating,
    setIsGenerating,
    generateComposition,
    structure,
    setStructure,
    addSection,
    removeSection,
    updateSection,
    currentEnsemble,
    setCurrentEnsemble,
    currentSubGenre,
    setCurrentSubGenre,
    traitOverrides,
    setTraitOverride,
    registerNoteListener,
    engine
  } = useMusicEngine();

  const {
      isReady,
      startAudio,
      play,
      stop,
      seek,
      isPlaying,
      setMasterVolume: setEngineVolume,
      setEnsemble: setAudioEnsemble,
      getTransportTime,
      getTransportPosition,
      setMacro // NEW
  } = useAudioEngine();

  const ensemble = useEnsemble();

  const [settings, setSettings] = useState({
    tempo: 110,
    // New Complexity Vectors (Default 50)
    rhythmicDensity: 50,
    harmonicFreedom: 50,
    melodicRange: 50,
    timbralMotion: 50,
    // Legacy (Deprecated but kept for safety if needed internally)
    globalComplexity: 60,
    dynamics: 75,
    swing: 0,
    reverb: 30
  });

  const [rootKey, setRootKey] = useState("E");
  const [scaleType, setScaleType] = useState("Auto");
  const [masterVolume, setMasterVolume] = useState(0); // Default to 0dB as requested
  const [showStartOverlay, setShowStartOverlay] = useState(true);
  const [isDNAModalOpen, setDNAModalOpen] = useState(false);

  // --- PERSISTENCE LOGIC ---
  // Load State on Mount
  useEffect(() => {
      const saved = localStorage.getItem('midiseek_project_state');
      if (saved) {
          try {
              const data = JSON.parse(saved);
              if (data.settings) setSettings(data.settings);
              if (data.rootKey) setRootKey(data.rootKey);
              if (data.scaleType) setScaleType(data.scaleType);
              if (data.currentEnsemble) setCurrentEnsemble(data.currentEnsemble);
              if (data.currentSubGenre) setCurrentSubGenre(data.currentSubGenre);
              if (data.structure) setStructure(data.structure);

              // Restore active instruments if available
              if (Array.isArray(data.activeInstrumentIds) && data.activeInstrumentIds.length > 0) {
                  // We need to reconstruct the instrument objects from IDs
                  if (ensemble.loadFromIds) ensemble.loadFromIds(data.activeInstrumentIds);
              }
          } catch (e) {
              console.error("Failed to load project state:", e);
          }
      }
  }, []);

  // Save State on Change
  useEffect(() => {
      const activeInstrumentIds = ensemble.activeInstruments.map(inst => inst.id);
      const stateToSave = {
          settings,
          rootKey,
          scaleType,
          currentEnsemble,
          currentSubGenre,
          structure,
          activeInstrumentIds
      };
      localStorage.setItem('midiseek_project_state', JSON.stringify(stateToSave));
  }, [settings, rootKey, scaleType, currentEnsemble, currentSubGenre, structure, ensemble.activeInstruments]);

  useEffect(() => {
      setEngineVolume(masterVolume);
  }, [masterVolume, setEngineVolume]);

  useEffect(() => {
      setAudioEnsemble(ensemble.activeInstruments);
  }, [ensemble.activeInstruments, setAudioEnsemble]);

  const totalDuration = structure.reduce((acc, sec) => acc + (sec.bars || 4), 0) * 4 * (60 / settings.tempo);
  const totalBeats = structure.reduce((acc, sec) => acc + (sec.bars || 4) * 4, 0);

  const updateSetting = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleUpdateSectionLength = (index, newBars) => {
      if (typeof newBars !== 'number' || newBars <= 0) return;

      const newStructure = [...structure];
      if (newStructure[index]) {
          newStructure[index] = { ...newStructure[index], bars: newBars };
          setStructure(newStructure);
      }
  };

  const handleReorderSections = (newStructure) => {
      setStructure(newStructure);
  };

  const handleGenreChange = (newSubGenre, newEnsemble) => {
      const ensembleKey = newEnsemble || currentEnsemble;

      // Update State
      setCurrentSubGenre(newSubGenre);
      if (newEnsemble) setCurrentEnsemble(newEnsemble);

      const ensembleDef = GENRE_LIBRARY[ensembleKey];
      if (ensembleDef) {
          let defaults = {};

          // 1. Get Ensemble Defaults
          if (ensembleDef.defaults) {
              defaults = { ...ensembleDef.defaults };
          }

          // 2. Get Sub-Genre Defaults (Overrides)
          if (ensembleDef.subGenres && ensembleDef.subGenres[newSubGenre]) {
              const subDefaults = ensembleDef.subGenres[newSubGenre].defaults;
              if (subDefaults) {
                  defaults = { ...defaults, ...subDefaults };
              }
          }

          // 3. Apply Defaults
          if (defaults.bpm) updateSetting('tempo', defaults.bpm);
          if (defaults.key) setRootKey(normalizeKey(defaults.key)); // Normalize for UI
          if (defaults.scale) setScaleType(defaults.scale);
          if (defaults.timeSig) updateSetting('timeSig', defaults.timeSig);

          // Hard Reset Structure & Time Signature
          if (defaults.structure) {
              // Inject Time Signature into sections for visual feedback
              const timeSig = defaults.timeSig || 4; // Default to 4/4 if missing
              const enrichedStructure = defaults.structure.map(s => ({
                  ...s,
                  timeSignature: { numerator: timeSig, denominator: 4 }
              }));
              setStructure(enrichedStructure);
          } else {
              // Ensure default structure exists if none defined
               setStructure([
                { type: 'INTRO', bars: 4, timeSignature: { numerator: 4, denominator: 4 } },
                { type: 'VERSE', bars: 8, timeSignature: { numerator: 4, denominator: 4 } },
                { type: 'CHORUS', bars: 8, timeSignature: { numerator: 4, denominator: 4 } },
                { type: 'OUTRO', bars: 4, timeSignature: { numerator: 4, denominator: 4 } }
               ]);
          }

          // 4. Load Instruments (Preset)
          // Always reload preset when Ensemble Type changes to ensure "Full Orchestra" gets full orchestra
          if (newEnsemble && ensemble.loadPreset) {
              ensemble.loadPreset(newEnsemble, newSubGenre);
          }
      }
  };

  const handleStartAudio = async () => {
      const started = await startAudio();
      if (started) {
          setShowStartOverlay(false);
          if (ensemble.activeInstruments.length === 0) {
              ensemble.loadPreset('rock_band');
          }
      }
  };

  const handleNoteEvent = useCallback((ev) => {
      if (engine && engine.notifyNote) {
           const id = ev.instanceId || ev.instrumentId || ev.role;
           engine.notifyNote(id, ev);
      }
  }, [engine]);

  const handleGenerate = () => {
    // New Generation Logic using useMusicEngine (The "Smart" Engine)
    console.log("Generating with useMusicEngine...", { settings, rootKey, scaleType });
    setIsGenerating(true);

    const profile = {
        // Pass individual vectors
        rhythmicDensity: settings.rhythmicDensity,
        harmonicFreedom: settings.harmonicFreedom,
        melodicRange: settings.melodicRange,
        timbralMotion: settings.timbralMotion,
        // Legacy fallback
        globalComplexity: settings.globalComplexity,
        userScale: scaleType,
        ...traitOverrides
    };

    const context = {
        instruments: ensemble.activeInstruments,
        rootKey: rootKey,
        timeSig: 4 // Default to 4/4
    };

    // This calls internal generate(), updates state via setComposition, and returns the result
    const result = generateComposition(profile, context);

    if (result && result.events) {
        // Fix: Silent Fallback Bug
        // If the pipeline inferred new instruments, we must update the Ensemble state
        // so that the AudioEngine doesn't mark them as "stale" and dispose them.
        if (result.instruments && result.instruments.length > 0 && ensemble.loadInstruments) {
             console.log("[App] Updating ensemble with pipeline instruments:", result.instruments.length);
             ensemble.loadInstruments(result.instruments);
        }

        // play() expects composition object
        play(result, settings.tempo, handleNoteEvent);
    }
    setIsGenerating(false);
  };

  const handleTogglePlay = () => {
      if (isPlaying) {
          stop();
      } else {
          handleGenerate();
      }
  };

  const handleDownloadMidi = () => {
      if (!composition || !composition.events) {
          alert("Please generate a composition first.");
          return;
      }

      try {
          const midiData = exportToMidi(composition.events, settings.tempo);

          if (!midiData) {
              alert("Error generating MIDI data.");
              return;
          }

          const blob = new Blob([midiData], { type: 'audio/midi' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `midiseek_${currentSubGenre}_${Date.now()}.mid`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
      } catch (e) {
          console.error("MIDI Export failed:", e);
          alert("Failed to export MIDI: " + e.message);
      }
  };

  return (
    <div className="app-container" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {showStartOverlay && <StartOverlay onStart={handleStartAudio} />}

      <header style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
          borderBottom: '1px solid #333',
          paddingBottom: '1rem'
      }}>
        <h1 style={{ margin: 0, fontSize: '1.5rem', letterSpacing: '0.1em' }}>MIDI<span style={{ color: 'var(--color-primary)' }}>Seek</span></h1>

        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <button
                onClick={handleDownloadMidi}
                style={{
                    fontSize: '0.8rem',
                    padding: '8px 16px',
                    border: '1px solid var(--color-primary)',
                    color: 'var(--color-primary)',
                    background: 'transparent'
                }}
            >
                DOWNLOAD MIDI
            </button>
            <div style={{ width: '100px' }}>
                 <Knob
                    label="Master Vol"
                    value={masterVolume}
                    min={-60} max={0}
                    step={1}
                    size={40}
                    onChange={setMasterVolume}
                />
            </div>
        </div>
      </header>

      {/* NEW: Unified Global Control Bar */}
      <GlobalControlBar
          currentEnsemble={currentEnsemble}
          currentSubGenre={currentSubGenre}
          onGenreChange={handleGenreChange}
          rootKey={rootKey}
          setRootKey={setRootKey}
          scaleType={scaleType}
          setScaleType={setScaleType}
          tempo={settings.tempo}
          setTempo={(v) => updateSetting('tempo', v)}
          // New Vectors
          rhythmicDensity={settings.rhythmicDensity}
          setRhythmicDensity={(v) => updateSetting('rhythmicDensity', v)}
          harmonicFreedom={settings.harmonicFreedom}
          setHarmonicFreedom={(v) => updateSetting('harmonicFreedom', v)}
          melodicRange={settings.melodicRange}
          setMelodicRange={(v) => updateSetting('melodicRange', v)}
          timbralMotion={settings.timbralMotion}
          setTimbralMotion={(v) => updateSetting('timbralMotion', v)}
      />

      <div className="main-controls" style={{ padding: '0 20px 20px 20px' }}>

          {/* Arrangement Editor (Timeline) - Moved UP */}
          <ArrangementMatrix
            sections={structure}
            onAddSection={addSection}
            onRemoveSection={removeSection}
            onUpdateSection={updateSection}
            onReorderSections={handleReorderSections}
            getTransportTime={getTransportTime}
            totalDuration={totalDuration}
            isPlaying={isPlaying}
            events={composition ? composition.events : []}
          />

          <div style={{ display: 'flex', gap: '20px', alignItems: 'center', margin: '20px 0' }}>
               {/* Playback Controls */}
               <button
                  onClick={handleTogglePlay}
                  style={{
                      background: isPlaying ? 'var(--color-secondary)' : 'var(--color-primary)',
                      color: 'black',
                      border: 'none',
                      borderRadius: '50%',
                      width: '60px',
                      height: '60px',
                      fontSize: '1.5rem',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 0 15px rgba(0,229,255,0.3)'
                  }}
               >
                  {isPlaying ? '■' : '▶'}
               </button>
               <button
                  onClick={handleGenerate}
                  disabled={isGenerating}
                  style={{
                      padding: '10px 20px',
                      background: 'transparent',
                      border: '1px solid var(--color-primary)',
                      color: 'var(--color-primary)',
                      borderRadius: '4px',
                      cursor: 'pointer'
                  }}
               >
                  {isGenerating ? 'GENERATING...' : 'GENERATE NEW'}
               </button>
          </div>

          <PlaybackVisualizer
              ensemble={ensemble}
              engine={engine}
              structure={structure}
              events={composition ? composition.events : []}
              getTransportTime={getTransportTime}
              getTransportPosition={getTransportPosition}
              isPlaying={isPlaying}
              totalDuration={totalDuration}
              totalBeats={totalBeats}
              onSeek={seek}
          />
      </div>

      <main style={{ flex: 1, padding: '0 20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

        {/* NEW: Master Effects Panel */}
        <MasterEffectsPanel setMacro={setMacro} />

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px', backgroundColor: '#111', border: '1px solid #333', borderRadius: '4px' }}>
            <div>
                <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem', marginRight: '10px' }}>Active DNA:</span>
                <span style={{ color: 'var(--color-primary)', fontSize: '0.9rem', fontFamily: 'var(--font-tech)' }}>
                    H:{traitOverrides.harmony || 'Base'} / R:{traitOverrides.rhythm || 'Base'} / V:{traitOverrides.voicing || 'Base'}
                </span>
            </div>
            <button
                onClick={() => setDNAModalOpen(true)}
                style={{ padding: '8px 16px', fontSize: '0.8rem', borderColor: 'var(--color-secondary)', color: 'var(--color-secondary)' }}
            >
                EDIT DNA
            </button>
        </div>

        {ensemble.activeInstruments.filter(inst => inst.type && inst.type.startsWith('hardware')).map(inst => (
            <PerformanceControlPanel key={inst.instanceId} activeInstrument={inst} engine={engine} />
        ))}

        <div className="status-bar" style={{
            height: '30px',
            marginTop: '10px',
            marginBottom: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--color-primary)',
            fontFamily: 'var(--font-tech)',
            fontSize: '0.9rem'
        }}>
            {isGenerating ? 'GENERATING...' : (composition ? 'Composition Ready' : 'Ready')}
        </div>

        <ContactForm />

        <div style={{
            marginTop: '40px',
            marginBottom: '20px',
            display: 'flex',
            justifyContent: 'center',
            padding: '20px',
            borderTop: '1px solid #222'
        }}>
            <DonateButton />
        </div>

        <DNAModal
            isOpen={isDNAModalOpen}
            onClose={() => setDNAModalOpen(false)}
            traitOverrides={traitOverrides}
            setTraitOverride={setTraitOverride}
        />

      </main>

      <Footer />
    </div>
  );
}

export default App;
